import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import Pagination from "@mui/material/Pagination";
import notifications from "../../common/Components/Notifications/Notification";
import {
  allotNfcTags,
  importNfcTagCsv,
  listNfcTags,
  syncIxkioTags,
} from "../../services/hkpNfcTagApi";
import { getApi } from "../../helpers/auth_helpers";
import StudioFilterBar from "../../common/Components/FilterBar/StudioFilterBar";

const LIMIT = 50;

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const HkpNfcTags = () => {
  const authUser: any = readAuthUser();
  const isSuperAdmin = authUser?.users?.role === "SUPER_ADMIN";
  const [rows, setRows] = useState<any[]>([]);
  const [totCnt, setTotCnt] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [status, setStatus] = useState("");
  const [fromTagNo, setFromTagNo] = useState("");
  const [toTagNo, setToTagNo] = useState("");
  const [orgs, setOrgs] = useState<any[]>([]);
  const [allotOrg, setAllotOrg] = useState("");
  const [allotFrom, setAllotFrom] = useState("");
  const [allotTo, setAllotTo] = useState("");

  const loadOrgs = async () => {
    const json: any = await getApi(
      `${process.env.REACT_APP_SERVERURL}/organization/organizations?offset=0&limit=200`
    );
    setOrgs(json?.success && Array.isArray(json?.data?.docs) ? json.data.docs : []);
  };

  const loadTags = async (nextOffset = offset) => {
    setLoading(true);
    const json: any = await listNfcTags({
      organizationId,
      status,
      fromTagNo,
      toTagNo,
      offset: nextOffset,
      limit: LIMIT,
    });
    setLoading(false);
    if (json?.success) {
      setRows(Array.isArray(json.data?.docs) ? json.data.docs : []);
      setTotCnt(json.data?.length || 0);
    } else {
      notifications({
        title: "HKP tags",
        message: json?.message || "Could not load tags",
        type: "danger",
      });
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    loadOrgs();
    loadTags(0);
  }, [isSuperAdmin]);

  const pages = Math.max(1, Math.ceil(totCnt / LIMIT));

  if (!isSuperAdmin) {
    return (
      <div className="p-3">
        <h3>HKP tags</h3>
        <p>Only super admin can register and allot NFC tags.</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <h3>HKP tags</h3>
      <p>
        This page registers numeric NFC tag numbers, then allots a number range to a studio. Studios type the
        HKP tag number; HKP ID fills from this registry. QR labels generated in Physical Identity are allotted
        there, one carrier at a time, to a studio or a My Collection customer. <Link to="/physical-identities">Allot QR or NFC labels</Link>
      </p>
      <StudioFilterBar
        fields={[
          {
            id: "status",
            label: "Status",
            kind: "select",
            placeholder: "All statuses",
            value: status,
            options: [
              { label: "Registered", value: "REGISTERED" },
              { label: "Allotted", value: "ALLOTTED" },
              { label: "Bound", value: "BOUND" },
            ],
            onChange: (value: any) =>
              setStatus(typeof value === "object" ? value?.value || "" : value || ""),
          },
          {
            id: "fromTagNo",
            label: "From tag",
            placeholder: "From tag no",
            value: fromTagNo,
            onChange: (value: string) => setFromTagNo(value),
          },
          {
            id: "toTagNo",
            label: "To tag",
            placeholder: "To tag no",
            value: toTagNo,
            onChange: (value: string) => setToTagNo(value),
          },
        ]}
        defaultFieldId="fromTagNo"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            setOffset(0);
            loadTags(0);
          }
        }}
        onReset={() => {
          setOrganizationId("");
          setStatus("");
          setFromTagNo("");
          setToTagNo("");
          setOffset(0);
          loadTags(0);
        }}
        hideAdd
        moreCount={organizationId ? 1 : 0}
        moreContent={
          <div className="qf-field">
            <span className="qf-field-label">Studio</span>
            <Input type="select" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
              <option value="">All studios</option>
              {orgs.map((org) => (
                <option key={org._id || org.id} value={org._id || org.id}>
                  {org.name}
                </option>
              ))}
            </Input>
            <Button
              className="mt-3"
              onClick={() => {
                setOffset(0);
                loadTags(0);
              }}
            >
              Apply studio filter
            </Button>
          </div>
        }
        wideToolbar={
      <Row className="g-2 align-items-end">
        <Col xs={12} md={3}>
          <Label>Studio</Label>
          <Input type="select" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
            <option value="">All studios</option>
            {orgs.map((org) => (
              <option key={org._id || org.id} value={org._id || org.id}>
                {org.name}
              </option>
            ))}
          </Input>
        </Col>
        <Col xs={6} md={2}>
          <Label>Status</Label>
          <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="REGISTERED">Registered</option>
            <option value="ALLOTTED">Allotted</option>
            <option value="BOUND">Bound</option>
          </Input>
        </Col>
        <Col xs={6} md={2}>
          <Label>From tag no</Label>
          <Input value={fromTagNo} onChange={(e) => setFromTagNo(e.target.value)} />
        </Col>
        <Col xs={6} md={2}>
          <Label>To tag no</Label>
          <Input value={toTagNo} onChange={(e) => setToTagNo(e.target.value)} />
        </Col>
        <Col xs={6} md={3}>
          <Button
            onClick={() => {
              setOffset(0);
              loadTags(0);
            }}
          >
            Filter
          </Button>
        </Col>
      </Row>
        }
      />

      <Row className="g-2 align-items-end mt-4">
        <Col xs={12} md={3}>
          <Label>Allot to studio</Label>
          <Input type="select" value={allotOrg} onChange={(e) => setAllotOrg(e.target.value)}>
            <option value="">Select studio</option>
            {orgs.map((org) => (
              <option key={org._id || org.id} value={org._id || org.id}>
                {org.name}
              </option>
            ))}
          </Input>
        </Col>
        <Col xs={6} md={2}>
          <Label>From</Label>
          <Input value={allotFrom} onChange={(e) => setAllotFrom(e.target.value)} />
        </Col>
        <Col xs={6} md={2}>
          <Label>To</Label>
          <Input value={allotTo} onChange={(e) => setAllotTo(e.target.value)} />
        </Col>
        <Col xs={12} md={5}>
          <Button
            className="me-2"
            onClick={async () => {
              const from = Number(allotFrom);
              const to = Number(allotTo);
              if (!allotOrg || !Number.isInteger(from) || !Number.isInteger(to)) {
                notifications({
                  title: "Allot tags",
                  message: "Choose a studio and a numeric NFC tag range. QR serials such as HKP-000001 are allotted from Physical Identity.",
                  type: "danger",
                });
                return;
              }
              const res: any = await allotNfcTags({
                organizationId: allotOrg,
                fromTagNo: from,
                toTagNo: to,
              });
              notifications({
                title: "Allot tags",
                message: res?.message || (res?.success ? "Allotted" : "Allot failed"),
                type: res?.success ? "success" : "danger",
              });
              if (res?.success) loadTags(offset);
            }}
          >
            Allot range
          </Button>
          <Button
            className="me-2"
            onClick={async () => {
              const res: any = await syncIxkioTags();
              notifications({
                title: "ixkio sync",
                message: res?.message || (res?.success ? `Imported ${res?.data?.imported || 0}` : "Sync failed"),
                type: res?.success ? "success" : "danger",
              });
              if (res?.success) loadTags(0);
            }}
          >
            Sync ixkio
          </Button>
          <Label className="mb-0">
            Import CSV
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const res: any = await importNfcTagCsv(file);
                const errors = res?.data?.errors || [];
                notifications({
                  title: "CSV import",
                  message: !res?.success
                    ? res?.message || "Import failed"
                    : errors.length
                    ? errors.slice(0, 3).join("; ")
                    : `Imported ${res?.data?.imported || 0} tags`,
                  type: !res?.success || errors.length ? "danger" : "success",
                });
                e.target.value = "";
                loadTags(0);
              }}
            />
          </Label>
        </Col>
      </Row>

      <div className="mt-4">
        {loading ? <p>Loading…</p> : null}
        {!loading && rows.length === 0 ? <p>No tags in this filter.</p> : null}
        {rows.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th>Tag no</th>
                <th>HKP ID</th>
                <th>Studio</th>
                <th>Status</th>
                <th>Bound product</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || row.tagNo}>
                  <td>{row.tagNo}</td>
                  <td>{row.xuid}</td>
                  <td>{row.organizationName || "—"}</td>
                  <td>{row.status}</td>
                  <td>{row.boundProductUniqueId || "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        {totCnt > LIMIT ? (
          <Pagination
            count={pages}
            page={offset + 1}
            onChange={(_e, page) => {
              const next = page - 1;
              setOffset(next);
              loadTags(next);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default HkpNfcTags;
